import { useMutation, useQuery } from "@tanstack/react-query";
import { createContext, use, useEffect, useState, type JSX, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { User } from "../db/models.ts";
import { AccessGrant, StringEncodedAccessToken, URLSafeEncodedToken } from "../iam/schemas.ts";
import { useORPC } from "../orpc-react.tsx";

const OTP_QUERY_PARAMETER_NAME = "otp";

const AuthenticatedUserContext = createContext<User | undefined>(undefined);
export function useAuthenticatedUser(): User {
  const authenticatedUser = use(AuthenticatedUserContext);

  if (!authenticatedUser) throw new Error(`You are not logged in!`);

  return authenticatedUser;
}

interface AuthenticatorProps {
  children?: ReactNode;
  onAccessGranted?: (newAccessGrant: AccessGrant) => unknown;
}

export default function Authenticator(props: AuthenticatorProps): JSX.Element {
  const { children, onAccessGranted } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const otp = URLSafeEncodedToken.safeParse(searchParams.get(OTP_QUERY_PARAMETER_NAME)).data;

  const [accessToken, setAccessToken] = useState(getCachedAccessToken);

  const orpc = useORPC();

  const accessRequest = useMutation(
    orpc.iam.requestAccess.mutationOptions({
      onError: (error, { signInToken: otp }) => {
        console.error(`Failed to obtain access grant using otp ${otp}`, error);
      },
      onSuccess: (accessGrant, { signInToken: otp }) => {
        const { accessToken, grantedBy } = accessGrant;

        console.debug(`Received access token ${accessToken} from ${grantedBy}.`);

        setAccessToken(cacheAccessToken(accessToken));

        // if (!params.has(OTP_QUERY_PARAMETER_NAME)) return params;
        setSearchParams((params) => {
          if (!params.has(OTP_QUERY_PARAMETER_NAME)) return params;
          params.delete(OTP_QUERY_PARAMETER_NAME);
          return new URLSearchParams(params);
        });

        onAccessGranted?.(accessGrant);
      },
    }),
  );

  const me = useQuery(orpc.iam.whoAmI.queryOptions({ input: {} }));

  const authenticatedUser = me.data
    ? User.parse({
        ...me.data,
        createdAt: new Date(me.data.createdAt),
        updatedAt: new Date(me.data.updatedAt),
      })
    : undefined;

  // If we have an OTP, send an access request to exchange it for an access
  // token. If successful, the resulting access token will be used
  useEffect(() => {
    if (!otp) return;

    accessRequest.mutate({ signInToken: otp });
    console.debug(`Sent access request using OTP ${otp}.`);

    setSearchParams((params) => {
      if (!params.has(OTP_QUERY_PARAMETER_NAME)) return params;
      params.delete(OTP_QUERY_PARAMETER_NAME);
      console.debug(`Used and removed OTP ${otp} from URL search params.`);
      return new URLSearchParams(params);
    });
  }, [otp, setSearchParams]);

  return (
    <AuthenticatedUserContext.Provider value={authenticatedUser}>
      {children}
    </AuthenticatedUserContext.Provider>
  );
}

export function getCachedAccessToken(): StringEncodedAccessToken | undefined {
  let stored = localStorage.getItem("heliopolis-access-token") ?? undefined;
  if (stored) stored = StringEncodedAccessToken.safeParse(stored).data;

  return stored;
}

function cacheAccessToken(token: StringEncodedAccessToken): StringEncodedAccessToken {
  if (localStorage.getItem("heliopolis-access-token") === token) return token;

  localStorage.setItem("heliopolis-access-token", token);

  return token;
}
